/**
 * Code QR d'une image choisie, collee ou deposee par l'utilisateur (capture d'ecran de la page d'activation d'une
 * double authentification, photo d'etiquette recadree) : l'image est lue SUR LE POSTE, jamais envoyee, et ses pixels
 * sont effaces des qu'ils ont servi.
 *
 * PNG (le format de toute capture d'ecran) : `png-decode.ts`, pur. Autres formats : le canevas du navigateur
 * (`createImageBitmap`), fond blanc sous la transparence comme pour un PNG.
 */
import { parseTotpSeed } from '@klavox/crypto-core';
import { decodePngLuminance, isPng } from './png-decode.js';
import { readQrImage, type LuminanceImage, type QrReadResult } from './qr-decode.js';
import { looksLikeWifiQr } from './wifi-qr.js';

/** 40 millions de pixels : une capture 8K (33 Mpx) passe, une image forgee de 100 000 x 100 000 non. */
export const MAX_QR_IMAGE_PIXELS = 40_000_000;
/** Un fichier image au-dela de 50 Mo n'est pas une capture d'ecran. */
export const MAX_QR_IMAGE_BYTES = 50 * 1024 * 1024;

export const QR_IMAGE_UNSUPPORTED = 'Image illisible : choisissez une capture d’ecran (PNG, JPEG ou WebP).';

/** Premiere image d'une liste de fichiers (presse-papiers, depot, selecteur), ou `null`. */
export function firstImageFile(files: Iterable<File> | ArrayLike<File> | null | undefined): File | null {
  if (files === null || files === undefined) return null;
  for (const file of Array.from(files as ArrayLike<File>)) if (file.type.startsWith('image/')) return file;
  return null;
}

/** Niveaux de gris de pixels RGBA (canevas, image de camera) ; les pixels source sont effaces une fois convertis. */
export function rgbaLuminance(pixels: { readonly width: number; readonly height: number; readonly data: Uint8ClampedArray }): LuminanceImage {
  const { width, height, data } = pixels;
  const luminance = new Uint8Array(width * height);
  for (let i = 0; i < luminance.length; i++) {
    luminance[i] = Math.round(0.299 * data[i * 4]! + 0.587 * data[i * 4 + 1]! + 0.114 * data[i * 4 + 2]!);
  }
  data.fill(0);
  return { width, height, luminance };
}

async function canvasLuminance(bytes: Uint8Array, type: string): Promise<LuminanceImage> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') throw new Error(QR_IMAGE_UNSUPPORTED);
  const bitmap = await createImageBitmap(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  try {
    const { width, height } = bitmap;
    if (width * height > MAX_QR_IMAGE_PIXELS) throw new Error('image trop grande');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) throw new Error(QR_IMAGE_UNSUPPORTED);
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0);
    const pixels = context.getImageData(0, 0, width, height);
    const image = rgbaLuminance(pixels);
    context.clearRect(0, 0, width, height);
    canvas.width = 0;
    return image;
  } finally {
    bitmap.close();
  }
}

/** Lit le code QR des octets d'une image ; tout echec est dit en clair, sans jamais citer le contenu. */
export async function readQrFromImageBytes(bytes: Uint8Array, type: string): Promise<QrReadResult> {
  if (bytes.length > MAX_QR_IMAGE_BYTES) return { ok: false, problem: 'Image trop lourde pour une capture d’ecran (50 Mo au plus).' };
  let image: LuminanceImage;
  try {
    image = isPng(bytes) ? await decodePngLuminance(bytes, MAX_QR_IMAGE_PIXELS) : await canvasLuminance(bytes, type);
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    return { ok: false, problem: message === 'image trop grande' ? 'Image trop grande (40 millions de pixels au plus).' : QR_IMAGE_UNSUPPORTED };
  } finally {
    bytes.fill(0);
  }
  try {
    return readQrImage(image);
  } finally {
    image.luminance.fill(0);
  }
}

export type TotpQrReading =
  | { readonly ok: true; readonly seed: string; readonly issuer: string; readonly account: string }
  | { readonly ok: false; readonly problem: string };

/**
 * Graine d'un code QR de double authentification : l'URI `otpauth://totp/...` ENTIERE (periode, chiffres et
 * algorithme gardes, comme si elle etait collee), son emetteur et son compte (Key URI Format). Un code HOTP, un
 * transfert Google Authenticator, un code Wi-Fi ou un autre texte sont refuses en le disant -- sans jamais repeter
 * le texte lu, qui peut etre un secret.
 */
export function totpFromQrText(text: string): TotpQrReading {
  const value = text.trim();
  if (/^otpauth-migration:/iu.test(value)) {
    return { ok: false, problem: 'Ce code QR transfere des comptes de Google Authenticator : il s’importe depuis l’ecran Importer (« Lire des captures de codes QR », avec chaque code du transfert), pas dans une entree.' };
  }
  if (/^otpauth:\/\/hotp\//iu.test(value)) {
    return { ok: false, problem: 'Ce code QR active un code HOTP (a compteur) : Klavox ne genere que des codes TOTP (a duree).' };
  }
  if (/^otpauth:\/\/totp\//iu.test(value)) {
    try {
      parseTotpSeed(value);
    } catch {
      return { ok: false, problem: 'Ce code QR de double authentification porte une graine invalide.' };
    }
    const url = new URL(value);
    let label = '';
    try {
      label = decodeURIComponent(url.pathname.replace(/^\/+/u, ''));
    } catch {
      label = '';
    }
    const colon = label.indexOf(':');
    const prefix = colon >= 0 ? label.slice(0, colon).trim() : '';
    const account = (colon >= 0 ? label.slice(colon + 1) : label).trim();
    const issuer = (url.searchParams.get('issuer') ?? '').trim() || prefix;
    return { ok: true, seed: value, issuer, account };
  }
  if (looksLikeWifiQr(value)) return { ok: false, problem: 'Ce code QR est celui d’un reseau Wi-Fi, pas d’une double authentification.' };
  return { ok: false, problem: 'Ce code QR ne porte pas de graine de double authentification (otpauth://totp/…).' };
}

/**
 * Captures des codes QR « Transferer des comptes » de Google Authenticator (un gros transfert en compte plusieurs),
 * ou de codes `otpauth://` : le texte de chaque code, une ligne par code, pret pour `parseAuthenticatorUriList`.
 * Tout ou rien : une capture illisible ou etrangere est dite par son nom, sans jamais citer ce qu'elle porte, et
 * rien n'est importe.
 */
export async function authenticatorUrisFromImages(files: readonly File[]): Promise<{ readonly ok: true; readonly text: string } | { readonly ok: false; readonly problem: string }> {
  const lines: string[] = [];
  for (const file of files) {
    const result = await readQrFromImageBytes(new Uint8Array(await file.arrayBuffer()), file.type);
    if (!result.ok) return { ok: false, problem: `« ${file.name} » : ${result.problem}` };
    const text = result.text.trim();
    if (!/^otpauth(-migration)?:\/\//iu.test(text) || /[\r\n]/u.test(text)) {
      return { ok: false, problem: `« ${file.name} » : ce code QR n’est ni un transfert Google Authenticator ni une graine otpauth://.` };
    }
    lines.push(text);
  }
  if (lines.length === 0) return { ok: false, problem: QR_IMAGE_UNSUPPORTED };
  return { ok: true, text: lines.join('\n') };
}
